import { useEffect } from 'react';
import { useAuth } from '../AuthModule';

const API = process.env.REACT_APP_API_URL || '';

function scrollToHash() {
  if (typeof window === 'undefined') return;
  const id = (window.location.hash || '').replace(/^#/, '').trim();
  if (!id) return;
  requestAnimationFrame(() => {
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

function scrollOhjeetToTop() {
  if (typeof window === 'undefined') return;
  const el = document.getElementById('ohjeet-top');
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  if (window.location.hash) {
    const path = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(null, '', path);
  }
}

/**
 * Logged-in help: käyttäjäpaneeli, chat, forum, tapahtumat, tilastot, kauppa.
 * Deep links: /ohjeet#krediittikauppa, /ohjeet#tuki, /ohjeet#pelitapahtumat, …
 * Beta-etusivu käyttää ohjeetBase={"/beta-testaajaksi/ohjeet"}.
 */
export default function OhjeetPage({ ohjeetBase = '/ohjeet' }) {
  const { user, patchUser } = useAuth();

  useEffect(() => {
    try {
      sessionStorage.removeItem('pulinaHighlightOhjeet');
    } catch {
      // ignore — legacy session highlight (replaced by users.first_timer)
    }
    scrollToHash();
    const onHash = () => scrollToHash();
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    if (!user?.id || user.firstTimer !== true) return;
    fetch(`${API}/api/users/${encodeURIComponent(user.id)}/ohjeet-first-visited`, { method: 'POST' })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) patchUser({ firstTimer: false });
      })
      .catch(() => {});
  }, [user?.id, user?.firstTimer, patchUser]);

  return (
    <div className="App-block OhjeetPage">

    <div className="OhjeetPage-backTopWrap">
        <button
          type="button"
          className="Button OhjeetPage-backTop"
          onClick={scrollOhjeetToTop}
          aria-label="Takaisin sivun alkuun"
        >
          Takaisin ylös
        </button>
      </div>

      <div className="OhjeetPage-container">
      <h1 id="ohjeet-top" className="OhjeetPage-title" tabIndex={-1}>
        Ohjeet
      </h1>
      <p className="OhjeetPage-lead">
        Lyhyt opas Pulinaan:</p>

      <nav className="OhjeetPage-toc" aria-label="Sisällysluettelo">
        <a href="#kayttajapaneeli">Käyttäjäpaneeli</a>
        <a href="#chat">Chat</a>
        <a href="#forum">Ilmoitustaulu</a>
        <a href="#tapahtumat">Tapahtumat</a>
        <a href="#levyraati">Levyraati</a>
        <a href="#keskustelu">Keskustelu</a>
        <a href="#tapahtuma-arvostelu">Tapahtuma-arvostelu</a>
        <a href="#tilastot">Tilastot</a>
        <a href="#krediittikauppa">Kauppa</a>
        <a href="#tuki">Tuki</a>
      </nav>

      <section className="OhjeetPage-section" id="kayttajapaneeli" aria-labelledby="ohjeet-kayttajapaneeli">
        <h2 id="ohjeet-kayttajapaneeli">Käyttäjäpaneeli</h2>
        <p>
           Kun olet kirjautunut sisään, valikossa oikealla näkyy profiilikuva tai näyttönimi. Sitä klikkaamalla pääset{' '}
          <strong>käyttäjätilin asetuksiin</strong> missä näkyy myös
          vaakasuora <strong>käyttäjäpaneeli</strong>:<br /> pikalinkit asetuksiin, profiiliin, viesteihin, kavereihin,
          tilastoihin (kun avattu) ja jäsenille kauppaan.
        </p>

        <h3 id="asetukset">Käyttäjäasetukset (Asetukset)</h3>
        <p>
          Sivulla <a href="/asetukset">/asetukset</a> voit muun muassa asettaa <strong>näyttönimen</strong>, valita{' '}
          <strong>teeman</strong> (vaalea / tumma), päivittää <strong>sähköpostin</strong> ja <strong>verkko-osoitteen</strong>,
          hallita <strong>uutiskirjeen</strong> tilausta sekä säätää profiiliin liittyviä asetuksia (avatar, värit, kuvaukset).
        </p>

        <h3 id="profiili">Käyttäjäprofiili (Profiili)</h3>
        <p>
          <a href="/profiili">Profiili-sivulla</a> näet, miltä tietosi näyttävät ja mitä muille paljastuu näkyvyyskytkimien
          perusteella. Otsikon vieressä oleva <strong>lasit</strong>-kuvake (Glasses) avaa <strong>julkisen profiilisivusi</strong>{' '}
          sellaisena kuin muut sen näkevät (linkki muodossa <code>/profiili/Näyttönimesi</code>, kun näyttönimi on asetettu).
        </p>

        <h3 id="viestit">Käyttäjäviestit (Viestit)</h3>
        <p>
          <a href="/postilaatikko">Postilaatikko</a> on yksityinen saapuneet-viestilaatikko. Sinne tulee viestejä
          esimerkiksi kun ostat tuotteita{' '}
          <a href={`${ohjeetBase}#krediittikauppa`}>kaupasta</a>, saat yksityisviestin (DM) tai saat tunnustusta
          peleissä ja tapahtumissa — ks.{' '}
          <a href={`${ohjeetBase}#pelitapahtumat`}>Pelitapahtumat ja palkinnot</a>.
        </p>
      </section>

      <section className="OhjeetPage-section" id="chat" aria-labelledby="ohjeet-chat">
        <h2 id="ohjeet-chat">Chat</h2>
        <p>
          <strong>Aulachat</strong> on osoitteessa <a href="/chat/">/chat/</a>. Tapahtuman striimin yhteydessä chat
          näkyy tapahtuman sivulla; erillisessä tapahtuman chat-näkymässä on sama musiikkisoitin kuin aulassa.
        </p>
        <p>
          Chat-palkissa on <strong>musiikkisoitin</strong>: se hakee satunnaisen Audius-kappaleen ja toistaa sitä.
          Voit käynnistää tai pysäyttää toiston (▶ / ■), hypätä <strong>seuraavaan</strong> biisiin (nappi{' '}
          <span className="OhjeetPage-mono">›</span>) ja avata <strong>lisätietoja</strong> (painike, jossa on kirjain{' '}
          <span className="OhjeetPage-mono">i</span>) — sieltä näet kappaleen tiedot ja voit avata sen Audiuksessa.
        </p>
        <p>
          Kirjoita <code>{ohjeetBase}</code> nähdäksesi slash-komennot. Voit myös jutella <strong>Pulinabotille</strong>{' '}
          chatissa (esimerkiksi mainitsemalla botin tai käyttämällä sille tarkoitettuja komentoja, riippuen
          tapahtumasta).
        </p>
      </section>

      <section className="OhjeetPage-section" id="forum" aria-labelledby="ohjeet-forum">
        <h2 id="ohjeet-forum">Ilmoitustaulu (”headless” -foorumi)</h2>
        <p>
          <a href="/forum/">Ilmoitustaulu</a> on ilman perinteisiä foorumiryhmiä: yksi lista ilmoituksia. Hakutoiminto tukee{' '}
          <strong>tekoälypohjaista hakua</strong> (painike ”Tekoälyhaku”), joka auttaa löytämään relevantteja viestejä.
          Pulinabotti voi hyödyntää foorumin sisältöä vastatessaan chatissa.
        </p>
      </section>

      <section className="OhjeetPage-section" id="tapahtumat" aria-labelledby="ohjeet-tapahtumat">
        <h2 id="ohjeet-tapahtumat">Tapahtumat</h2>
        <p>
          Etusivulta löytyy plus-nappi, jolla voit luoda tapahtuman. Tapahtuman tyyppi voi olla {':'}
          <strong>keskustelu</strong> sopii keskusteluun ilman videota tai
          videon kanssa<br />
          <strong>Peli</strong> Jos olet lunastanut itsellesi riittävät oikeudet, voit luoda pelitapahtumia. Voit aina liittyä pelitapahtumiin,<br />
          mutta et nää niitä ilman oikeita "UNLOCK" ostoja (ostetaan krediiteillä)<br />
          <strong>Levyraati</strong> Riittävällä oikeudella, voit luoda levyraadin, johon sinä itse valitset kappaleet. Osallistujat "raativat" tapahtuman kappaleita.          </p>
        <p>
          <strong>
            <em>Suoran striimin URL-osoite (esim. RTMP/HLS -syöte lomakkeesta) ei ole vielä toteutettu.</em>
          </strong>
        </p>
      </section>

      <section className="OhjeetPage-section" id="pelitapahtumat" aria-labelledby="ohjeet-pelitapahtumat">
        <h2 id="ohjeet-pelitapahtumat">Pelitapahtumat ja palkinnot</h2>
        <p>
          Peli- ja muissa tapahtumissa voit ansaita Pulina-krediittejä ja saada palautetta. Onnistumisista ja ostoista
          voi tulla ilmoituksia <a href={`${ohjeetBase}#viestit`}>postilaatikkoon</a>.
        </p>
      </section>

      <section className="OhjeetPage-section" id="levyraati" aria-labelledby="ohjeet-levyraati">
        <h2 id="ohjeet-levyraati">Levyraati</h2>
        <p>
          Levyraati on erikoinen tapahtuma, jonka aikana osallistuvat käyttäjät voivat arvostella Levyraati-tapahtumaan
          lisättyjä kappaleita. Jokaiselle kappaleelle "100% palaute" tarkoittaa tähtiarvostelua 1-5 sekä tekstimuotoista
          palautetta.
        </p>
        <p>
          Käyttäjät, jotka ovat antaneet 100% palautteen tapahtuman kaikille kappaleille, saavat palkkioksi <em>2PC *
          kappaleiden määrän</em>. Levyraadissa voi olla max. 10 kappaletta.
        </p>
      </section>

      <section className="OhjeetPage-section" id="keskustelu" aria-labelledby="ohjeet-keskustelu">
        <h2 id="ohjeet-keskustelu">Keskustelu-tapahtuma</h2>
        <p>
          Tapahtumatyypeistä monipuolisin - ja luultavasti hyödyllisin, on Keskustelu-tapahtuma.<br /> Tapahtumassa "Keskustelu"
          voi videon tuoda joko valmiina joltain alustalta( ns. "Esittely" ) tai striimiosoitteen kautta (ns.
          "live-tapahtuma" )<br />
          <br />
           Tai voi luoda Keskustelutapahtuman ilman videota ja keskittyä vain keskustelemaan - joko
          jostain teemasta ta ihan ilman teemaa - ihan up yours :)<br />
          <br />
          Jokaiselle keskustelutaphtumaan osallistuvalle (jotka ovat olleet paikalla yli 10 minuuttia) lähetetään tapahtumanarviointiviesti.<br />
          Näin tilaisuuden järjestäjä saa palautetta tapahtumasta ja arvion antaja saa krediittejä.
        </p>
      </section>

      <section className="OhjeetPage-section" id="tapahtuma-arvostelu" aria-labelledby="ohjeet-tapahtuma-arvostelu">
        <h2 id="ohjeet-tapahtuma-arvostelu">Tapahtuma-arvostelu</h2>
        <p>
          Keskustelu-tyypin tapahtuma voi käytännössä olla mitä vain. Striimi, jossa esitellään maalauksia, lausutaan
          runoja tai opetetaan tms. jne. Keskustelu-tapahtuma voi myös olla pelkkää keskustelua - keskusteluun liittyvällä videolla tai ilman. 
          On siis luonnollista, että tämän tyyppisistä tapahtumista olisi hostaajalle
          hyötyä, jos siitä saisi palautetta.
        </p>
        <p>
          Järjestelmä laskee kunkin osallistujan läsnäolominuutteja ja 10 läsnäolo minuutin jälkeen, lähettää osallistujalle
          sisäisen viestin, jossa pyydetään antamaan palautetta- Tapahtuma-arvostelun antamisesta käyttäjä ansaitsee 2PC
        </p>
      </section>

      <section className="OhjeetPage-section" id="tilastot" aria-labelledby="ohjeet-tilastot">
        <h2 id="ohjeet-tilastot">Käyttäjätilastot (Tilastot)</h2>
        <p>
          Välilehti <strong>Tilastot</strong> (<a href="/tilastot">/tilastot</a>) avautuu, kun olet ostanut
          kaupasta tuotteen, joka <strong>avaa tilastot</strong> (tuoteperheessä ”UNLOCK: Statistics” / vastaava
          nimitys kaupassa).
        </p>
        <p>
          Tilastoissa näet muun muassa foorumille kirjoittamasi viestit, <strong>krediittikirjanpidon</strong> sekä
          tapahtumien arvioita ja palautetta. Lisäosto <strong>tilastot+</strong> (tuote-ID: advanced statistics -unlock
          kaupassa) laajentaa näkymää: mm. päivän kysymyksiin liittyvät erittelyt ja{' '}
          <strong>levyraati-tapahtumien</strong> kappaletason arviointitiedot, kun ne ovat käytössä tililläsi.
        </p>
      </section>

      <section className="OhjeetPage-section" id="krediittikauppa" aria-labelledby="ohjeet-kauppa">
        <h2 id="ohjeet-kauppa">Kauppa (Pulina Credits)</h2>
        <p>
          Sovelluksessa ja yhteisössä aktiivisena ansaitset <strong>Pulina Credits (PC)</strong>. Krediiteillä ostat
          kaupasta (<a href="/kauppa">/kauppa</a>, vain jäsenille) tuotteita: osa <strong>avaa ominaisuuksia</strong>{' '}
          (esim. tilastot-välilehti), osa tukee palvelua ja osa on viihdettä tai kosmetiikkaa.
        </p>
      </section>

      <section className="OhjeetPage-section" id="tuki" aria-labelledby="ohjeet-tuki">
        <h2 id="ohjeet-tuki">Tuki</h2>
        <p>
          Jos olet kirjautunut sisään Pulinaan, valikkopalkissa näkyy Discord-painike. Logoa
          klikkaamalla voit liittyä<strong>Pulina-Discord</strong>-palvelimelle, jossa voit pyytää tukea, keskustella
          muiden kanssa ja ehdottaa parannuksia palveluun.
        </p>
      </section>
      </div>
    </div>
  );
}
